import { useId, useState, type FormEvent } from "react";
import { useLocation } from "wouter";
import { useCreateProject } from "../../api/queries";
import type { Project } from "../../api/types";
import { Button } from "../../components/Button";
import { Modal } from "../../components/Modal";
import { useToast } from "../../toast/ToastContext";
import "../../components/Form.css";

interface CreateProjectModalProps {
  open: boolean;
  onClose: () => void;
  /** Overrides where a successful create lands, for a caller with its own opinion about
   * the right next screen (HomePage's "Develop it" card, decision 6: a tenant with no
   * projects yet lands straight on the new project's Sessions tab with the develop
   * launcher open — see LAUNCH_DEVELOP_PARAM). Every other caller (GalleryPage,
   * ProjectsListPage) omits this and gets the default Overview/Services landing below —
   * one create flow, not a second hand-copied one (A-5). */
  postCreateHref?: (created: Project) => string;
}

export function CreateProjectModal({ open, onClose, postCreateHref }: CreateProjectModalProps) {
  const nameId = useId();
  const { pushToast } = useToast();
  const [, navigate] = useLocation();
  const mutation = useCreateProject();
  const [name, setName] = useState("");

  function handleClose() {
    setName("");
    mutation.reset();
    onClose();
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const submittedName = name.trim();
    mutation.mutate(
      { name: submittedName },
      {
        onSuccess: (created) => {
          pushToast({ title: `Project "${created.name}" created.`, tone: "success" });
          handleClose();
          navigate(
            postCreateHref
              ? postCreateHref(created)
              : `/projects/${created.id}`,
          );
        },
        // Deliberately no toast on error — the inline form-error below stays
        // legible while the operator fixes a name conflict.
      },
    );
  }

  const canSubmit = name.trim().length > 0 && !mutation.isPending;

  return (
    <Modal open={open} title="New project" onClose={handleClose} initialFocusId={nameId}>
      <form onSubmit={handleSubmit} noValidate>
        {mutation.isError && (
          <p className="form-error" role="alert">
            {mutation.error instanceof Error ? mutation.error.message : "Couldn't create this project."}
          </p>
        )}

        <label className="form-field" htmlFor={nameId}>
          <span>Name</span>
          <input
            id={nameId}
            type="text"
            autoComplete="off"
            spellCheck={false}
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            maxLength={63}
          />
        </label>

        <div className="form-actions">
          <Button type="button" variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={!canSubmit}>
            {mutation.isPending ? "Creating…" : "Create project"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
